import { useState } from 'react';
import { X, Save, Loader2, Wrench } from 'lucide-react';
import type { CreateMaintenanceDTO, MaintenanceType } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface MaintenanceFormProps {
  assetName: string;
  onSubmit: (data: CreateMaintenanceDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const MAINTENANCE_TYPES: { value: MaintenanceType; label: string; color: string }[] = [
  { value: 'PREVENTIVE', label: 'Preventivo', color: 'bg-blue-100 text-blue-700' },
  { value: 'CORRECTIVE', label: 'Correctivo', color: 'bg-red-100 text-red-700' },
  { value: 'INSPECTION', label: 'Inspección', color: 'bg-green-100 text-green-700' },
];

export const MaintenanceForm = ({
  assetName,
  onSubmit,
  onCancel,
  loading,
}: MaintenanceFormProps) => {
  const { t } = useI18n();

  const [formData, setFormData] = useState<CreateMaintenanceDTO>({
    type: 'PREVENTIVE',
    description: '',
    cost: undefined,
    performedBy: '',
    performedAt: new Date().toISOString().split('T')[0],
    nextMaintenanceAt: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description.trim()) return;
    await onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-slate-600" />
            <div>
              <h2 className="text-lg font-semibold text-slate-800">{t('inventory.registerMaintenance')}</h2>
              <p className="text-sm text-slate-500">{assetName}</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Maintenance Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('inventory.maintenanceType')} *
            </label>
            <div className="flex gap-2">
              {MAINTENANCE_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.type === type.value
                      ? type.color + ' ring-2 ring-offset-2 ring-primary-500'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('inventory.maintenanceDescription')} *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={3}
              required
              placeholder={t('inventory.maintenanceDescriptionPlaceholder')}
            />
          </div>

          {/* Performed By & Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('inventory.performedBy')}
              </label>
              <input
                type="text"
                value={formData.performedBy || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, performedBy: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('inventory.technicianName')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('inventory.performedAt')}
              </label>
              <input
                type="date"
                value={formData.performedAt || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, performedAt: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Cost & Next Maintenance */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('inventory.maintenanceCost')}
              </label>
              <input
                type="number"
                value={formData.cost || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value ? Number(e.target.value) : undefined }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('inventory.nextMaintenanceAt')}
              </label>
              <input
                type="date"
                value={formData.nextMaintenanceAt || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, nextMaintenanceAt: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.notes')}
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.description.trim()}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
